import { useEffect, useState } from "react";
import type { PortalApi } from "./api.ts";
import type { Identity, Role } from "./auth.ts";
import { Mark, PageIcon } from "./Icons.tsx";
import type { LiveFeed } from "./live.ts";
import { AccountsPage } from "./pages/Accounts.tsx";
import { MetricsPage } from "./pages/Metrics.tsx";
import { RunsPage, runLink } from "./pages/Runs.tsx";
import { Button, PageLayout } from "./ui.tsx";

/**
 * The portal's pages and who sees them (docs/roadmap.md#admin-portal): admins and operators see the
 * metrics and accounts; operators also the run browser, since capture data is read by operators
 * only (docs/security.md#roles). A page a role cannot use is not in its navigation, and its address shows
 * the first page instead; the data API refuses its operations anyway.
 */
export interface Page {
  readonly id: string;
  readonly label: string;
  readonly roles: readonly Role[];
}

export const PAGES: readonly Page[] = [
  { id: "metrics", label: "Metrics", roles: ["admin", "operator"] },
  { id: "accounts", label: "Accounts", roles: ["admin", "operator"] },
  { id: "runs", label: "Runs", roles: ["operator"] },
];

export function pagesFor(role: Role | undefined): Page[] {
  return role ? PAGES.filter((page) => page.roles.includes(role)) : [];
}

/** The page named by the address's hash (`#/accounts`), and what follows it. */
export function parseHash(hash: string): { page: string; rest: string[] } {
  const [page = "", ...rest] = hash.replace(/^#\/?/, "").split("/").map(decodeURIComponent);
  return { page, rest };
}

function useHash(): string {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const changed = () => setHash(window.location.hash);
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  return hash;
}

export interface AppProps {
  readonly api: PortalApi;
  readonly identity: Identity;
  readonly onSignOut?: () => void;
  /** The live state's subscriptions (docs/carrier-runs.md#live-state); polling without them. */
  readonly live?: LiveFeed;
}

export function App({ api, identity, onSignOut, live }: AppProps) {
  const hash = useHash();
  const pages = pagesFor(identity.role);
  const { page: wanted, rest } = parseHash(hash);
  const page = pages.find((candidate) => candidate.id === wanted) ?? pages[0];

  const who = identity.email ?? "signed in";
  return (
    <div className="min-h-screen md:grid md:grid-cols-[224px_minmax(0,1fr)]">
      <header className="sticky top-0 z-10 border-line border-b bg-sidebar md:static md:border-r md:border-b-0">
        <div className="flex flex-wrap items-center gap-x-2 px-3 pt-2 md:sticky md:top-0 md:h-screen md:flex-col md:flex-nowrap md:items-stretch md:gap-1 md:px-2 md:py-3">
          <strong className="flex items-center gap-2.5 whitespace-nowrap py-1 font-semibold md:px-2 md:pt-1.5 md:pb-3.5">
            <Mark />
            Quote Agent admin
          </strong>
          <nav
            aria-label="Pages"
            className="order-3 -mx-1 flex basis-full gap-0.5 overflow-x-auto pt-1 pb-1.5 md:order-none md:mx-0 md:basis-auto md:flex-col md:gap-px md:overflow-visible md:p-0"
          >
            {pages.map((candidate) => (
              <a
                key={candidate.id}
                href={`#/${candidate.id}`}
                aria-current={candidate.id === page?.id ? "page" : undefined}
                className="flex items-center gap-2.5 whitespace-nowrap rounded-md px-2 py-[5px] font-medium text-fg-2 hover:bg-hover hover:text-fg hover:no-underline aria-[current=page]:bg-active aria-[current=page]:font-semibold aria-[current=page]:text-fg"
              >
                <PageIcon id={candidate.id} />
                {candidate.label}
              </a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 md:mt-auto md:ml-0 md:border-line md:border-t md:pt-2.5 md:pr-1.5 md:pb-0.5 md:pl-2">
            <span
              className="grid size-6 flex-none place-items-center rounded-full bg-active font-semibold text-xs"
              aria-hidden="true"
            >
              {who.charAt(0).toUpperCase()}
            </span>
            {/* On a narrow screen, the email stays readable to assistive technology only. */}
            <span className="sr-only md:not-sr-only md:flex md:min-w-0 md:flex-1 md:flex-col md:leading-tight">
              <span className="truncate font-medium text-ui" title={who}>
                {who}
              </span>
              {identity.role && (
                <span className="text-muted text-xs capitalize">{identity.role}</span>
              )}
            </span>
            {onSignOut && (
              <Button variant="quiet" size="sm" type="button" onClick={onSignOut}>
                Sign out
              </Button>
            )}
          </div>
        </div>
      </header>
      <main className="w-full min-w-0 max-w-[1280px] px-4 pt-6 pb-16 md:px-8 md:pt-10 md:pb-24 2xl:px-12">
        {!page ? (
          <NoAccess identity={identity} />
        ) : page.id === "accounts" ? (
          <AccountsPage api={api} identity={identity} />
        ) : page.id === "runs" ? (
          <RunsPage api={api} path={rest} {...(live && { live })} />
        ) : (
          <MetricsPage
            api={api}
            {...(pages.some((candidate) => candidate.id === "runs") && { runLink })}
          />
        )}
      </main>
    </div>
  );
}

function NoAccess({ identity }: { identity: Identity }) {
  return (
    <PageLayout title="No access">
      <p className="max-w-[80ch]">
        {identity.role ? `Your account is a ${identity.role}. ` : "Your account has no role yet. "}
        The admin portal is for the agency's admins and for operators; ask the agency's admin if you
        need it.
      </p>
    </PageLayout>
  );
}
